import { useEffect, useRef, useState } from 'react'

import { DEFAULT_MASKED_PASSWORD } from '../hooks/useEditCamera'
import { useEditExternalAccount } from '../hooks/useEditExternalAccount'
import { Controller, UseFormMethods } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Checkbox,
  ColorScheme,
  Grid,
  GridItem,
  Text,
  TextInput,
} from '@hivewatch/dark'

import { ControlledSelect, ControlledTextInput } from '@/components/ui'
import { DeviceCameraEditQuery } from '@/graphql/generated/operations'
import { StreamingType } from '@/graphql/generated/schemas'
import { getStreamingTypeOptions } from '@/utils/forms/optionBuilders'

import { CameraFormDataTypeI } from '../../types/types'
import {
  getRTSPPathForUi,
  getStreamingProtocolOption,
  getStreamingTypeOption,
  shouldHandleRTSPFields,
  streamingProtocols,
  validateIPOrHostname,
  validatePort,
} from '../utils/utils'

interface DarkEditCameraLiveStreamingFieldsIProps {
  cameraData: DeviceCameraEditQuery
  colorScheme: ColorScheme
  isLoading: boolean
  form: UseFormMethods<CameraFormDataTypeI>
  shouldRequireAuthentication: boolean
  onRequiresAuthenticationChange: (isChecked: boolean) => void
}

export const DarkEditCameraLiveStreamingFields = ({
  cameraData,
  colorScheme,
  isLoading,
  form,
  shouldRequireAuthentication,
  onRequiresAuthenticationChange,
}: DarkEditCameraLiveStreamingFieldsIProps) => {
  const { t } = useTranslation()
  const [isUsernameUpdated, setIsUsernameUpdated] = useState(false)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const initializedStreamingCameraIdRef = useRef<string>()
  const initializedAuthenticationCameraIdRef = useRef<string>()
  const initialRequiresAuthenticationRef = useRef(
    cameraData?.device?.camera?.requiresAuthentication ??
      shouldRequireAuthentication
  )
  const { errors, control, trigger, setValue, watch } = form
  const streamingProtocol = watch('streamingProtocol')
  const streamingType = watch('streamingType')
  const externalAccountId = watch('externalAccountId')
  const rtspPath = watch('rtspPath')
  const rtspIp = watch('rtspIp')
  const rtspPort = watch('rtspPort')
  const rtspUsername = watch('rtspUsername')
  const {
    externalAccountOptions,
    shouldShowExternalAccount,
    shouldShowOrganizationId,
    organizationId,
    defaultExternalAccount,
  } = useEditExternalAccount({
    streamingType,
    externalAccountId,
    setValue,
    initialExternalAccountId: cameraData?.device?.camera?.externalAccount?.id,
  })
  const shouldShowRTSPFields = shouldHandleRTSPFields(streamingType?.value)
  const isOnPremiseCamera = streamingType?.value === StreamingType.OnPremise

  useEffect(() => {
    const cameraId = cameraData?.device?.camera?.id
    if (!cameraId || initializedStreamingCameraIdRef.current === cameraId)
      return

    setValue('streamingType', getStreamingTypeOption(cameraData))
    initializedStreamingCameraIdRef.current = cameraId
  }, [cameraData, setValue])

  useEffect(() => {
    if (shouldShowRTSPFields && isFirstLoad) {
      setValue('rtspPath', cameraData?.device?.camera?.rtspPath)
      setValue('rtspUsername', cameraData?.device?.camera?.rtspUsername)
      setValue('rtspIp', cameraData?.device?.camera?.rtspIp)
      setValue('rtspPort', cameraData?.device?.camera?.rtspPort)
      setValue('streamingProtocol', getStreamingProtocolOption(cameraData))
      if (cameraData?.device?.camera?.rtspUsername) {
        setValue('rtspPassword', DEFAULT_MASKED_PASSWORD)
      }
      setIsFirstLoad(false)
    }

    if (!isOnPremiseCamera) onRequiresAuthenticationChange(true)
  }, [
    cameraData,
    isFirstLoad,
    isOnPremiseCamera,
    onRequiresAuthenticationChange,
    setValue,
    shouldShowRTSPFields,
  ])

  useEffect(() => {
    const cameraId = cameraData?.device?.camera?.id
    if (!cameraId || initializedAuthenticationCameraIdRef.current === cameraId)
      return

    const isInitialAuthenticationRequired =
      cameraData?.device?.camera?.requiresAuthentication ??
      shouldRequireAuthentication
    setValue('requiresAuthentication', isInitialAuthenticationRequired)
    onRequiresAuthenticationChange(isInitialAuthenticationRequired)
    initializedAuthenticationCameraIdRef.current = cameraId
  }, [
    cameraData,
    onRequiresAuthenticationChange,
    setValue,
    shouldRequireAuthentication,
  ])

  const handleUsernameUpdated = () => {
    if (!isUsernameUpdated) {
      setValue('rtspPassword', '', { shouldDirty: true })
      setIsUsernameUpdated(true)
    }
    trigger(['rtspPassword'])
  }

  return (
    <Box>
      <Grid gap='16px' templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}>
        <GridItem>
          <ControlledSelect
            colorScheme={colorScheme}
            control={control}
            data-testid='devicesPage_editCameraModal_streamingType'
            hasWarning={Boolean(errors.streamingType)}
            isClearable
            label={t('devices:crud_streaming_type_label')}
            name='streamingType'
            options={getStreamingTypeOptions()}
            placeholder={t('devices:crud_streaming_type_select')}
            rules={{ required: true }}
            warningText={t('devices:crud_streaming_select_placeholder')}
          />
        </GridItem>

        {shouldShowExternalAccount && (
          <GridItem>
            <ControlledSelect
              colorScheme={colorScheme}
              control={control}
              data-testid='devicesPage_editCameraModal_externalAccount'
              isClearable
              label={t('devices:crud_streaming_external_account')}
              name='externalAccountId'
              options={externalAccountOptions}
              placeholder={t('devices:crud_streaming_external_account_select')}
            />
          </GridItem>
        )}

        {shouldShowExternalAccount && !externalAccountId && (
          <GridItem>
            <TextInput
              colorScheme={colorScheme}
              disabled
              label={t('devices:crud_default_account_label')}
              value={defaultExternalAccount?.label ?? ''}
              width='100%'
            />
          </GridItem>
        )}
        {shouldShowExternalAccount && shouldShowOrganizationId && (
          <GridItem>
            <TextInput
              colorScheme={colorScheme}
              disabled
              label={t('devices:crud_organization_id_label')}
              value={organizationId ?? ''}
              width='100%'
            />
          </GridItem>
        )}
      </Grid>

      {shouldShowRTSPFields && (
        <>
          <Grid
            gap='16px'
            mt='16px'
            templateColumns={{
              base: '1fr',
              md: 'repeat(2, 1fr)',
              xl: 'repeat(4, 1fr)',
            }}
          >
            <GridItem>
              <ControlledSelect
                colorScheme={colorScheme}
                control={control}
                data-testid='devicesPage_editCameraModal_streamingProtocol'
                hasWarning={Boolean(errors.streamingProtocol)}
                isClearable
                isDisabled={isLoading}
                label={t('devices:crud_protocol_label')}
                name='streamingProtocol'
                options={streamingProtocols}
                placeholder={t('devices:crud_protocol_select')}
                warningText={t('devices:crud_protocol_select')}
              />
            </GridItem>
            <GridItem>
              <ControlledTextInput
                colorScheme={colorScheme}
                control={control}
                data-testid='devicesPage_editCameraModal_rtspPath'
                errors={errors}
                label={t('devices:crud_rtsp_path_label')}
                name='rtspPath'
                placeholder={t('devices:crud_rtsp_path_placeholder')}
                width='100%'
              />
            </GridItem>
            <GridItem>
              <ControlledTextInput
                colorScheme={colorScheme}
                control={control}
                data-testid='devicesPage_editCameraModal_rtspIP'
                errors={errors}
                label={t('devices:crud_rtsp_ip_hostname_label')}
                name='rtspIp'
                placeholder={t('devices:crud_rtsp_ip_placeholder')}
                rules={{ validate: validateIPOrHostname }}
                width='100%'
              />
            </GridItem>
            <GridItem>
              <ControlledTextInput
                colorScheme={colorScheme}
                control={control}
                data-testid='devicesPage_editCameraModal_rtspPort'
                errors={errors}
                inputProps={{ inputMode: 'numeric' }}
                label={t('devices:crud_rtsp_port_label')}
                name='rtspPort'
                placeholder={t('devices:crud_rtsp_port_placeholder')}
                rules={{ validate: validatePort }}
                width='100%'
              />
            </GridItem>
          </Grid>

          {isOnPremiseCamera && (
            <Box mt='16px'>
              <Controller
                control={control}
                defaultValue={initialRequiresAuthenticationRef.current}
                name='requiresAuthentication'
                render={({ value, onChange }) => (
                  <Checkbox
                    checked={Boolean(value)}
                    colorScheme={colorScheme}
                    data-testid='editCameraLiveStreamingFields_requiresAuthToggle'
                    label={t('devices:crud_requires_authentication')}
                    onChange={(event) => {
                      onChange(event.target.checked)
                      onRequiresAuthenticationChange(event.target.checked)
                    }}
                  />
                )}
              />
            </Box>
          )}

          {shouldRequireAuthentication && (
            <Grid
              gap='16px'
              mt='16px'
              templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
            >
              <ControlledTextInput
                colorScheme={colorScheme}
                control={control}
                data-testid='devicesPage_editCameraModal_rtspUsername'
                errors={errors}
                label={t('devices:crud_rtsp_username_label')}
                name='rtspUsername'
                onValueChange={handleUsernameUpdated}
                placeholder={t('devices:crud_rtsp_username_placeholder')}
                width='100%'
              />
              <ControlledTextInput
                colorScheme={colorScheme}
                control={control}
                errors={errors}
                inputProps={{ type: 'password' }}
                label={t('devices:crud_rtsp_password_label')}
                name='rtspPassword'
                placeholder={t('devices:crud_rtsp_password_placeholder')}
                rules={{ required: Boolean(rtspUsername) }}
                width='100%'
              />
            </Grid>
          )}

          <Box mt='16px'>
            <Text
              fontSize='11px'
              fontWeight='600'
              opacity={0.7}
              textTransform='uppercase'
            >
              {t('devices:crud_rtsp_url_label')}
            </Text>
            <Text
              fontSize='14px'
              fontWeight='600'
              mt='4px'
              wordBreak='break-all'
            >
              {getRTSPPathForUi(
                streamingProtocol?.value,
                rtspIp,
                rtspPort,
                rtspPath
              )}
            </Text>
          </Box>
        </>
      )}
    </Box>
  )
}
